import { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';

/**
 * SlideoutPortal
 * A specialized portal component designed to handle the mounting/unmounting handshake
 * required for smooth CSS animations (slide-ins/slide-outs).
 *
 * --- WHY WE USE THIS ---
 * Standard React conditional rendering (`show && <Component />`) unmounts the component
 * instantly, which kills any exit animations. This component stays mounted until the
 * child component signals that its animation is finished via the `unmountSlideout` callback.
 *
 * Inversely, we would like to not mount slideouts to the DOM prior to a user-driven interaction
 * that generates those slideouts. This also gets ahead of multiple event listeners per slideout.
 *
 * @param {boolean} show - The Source of Truth for visibility (usually driven by the Router/URL).
 * @param {function} children - A render prop that provides the `handleExited` callback.
 *
 * @example
 * <SlideoutPortal show={shouldShowDetails}>
 *   {(unmountSlideout) => (
 *     <MySlideoutComponent
 *       show={shouldShowDetails}
 *       unmountSlideout={unmountSlideout}
 *     />
 *   )}
 * </SlideoutPortal>
 */
const SlideoutPortal = ({ show, children }) => {
  const [shouldRender, setShouldRender] = useState(show);

  useEffect(() => {
    if (show) setShouldRender(true);
  }, [show]);

  const handleExited = () => setShouldRender(false);

  if (!shouldRender) return null;

  return ReactDOM.createPortal(children(handleExited), document.body);
};

export default SlideoutPortal;
